import { useState } from 'react'
import { format, parseISO } from 'date-fns'
import { ko } from 'date-fns/locale'
import {
  RefreshCw,
  Sparkles,
  MessageSquare,
  Clock,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  Lightbulb,
  Target,
  Users,
  History,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PageLoading } from '@/components/shared/Loading'
import { useAIInsights, useAIInsightsHistory, useAIInsightById } from '@/hooks/useAIInsights'
import { cn } from '@/utils/cn'
import type {
  AIInsightsResult,
  AIInsightsSummary,
  KeyInsight,
  TrendingTopic,
  NotablePattern,
  Recommendation,
  ImpactLevel,
  PriorityLevel,
  TrendDirection,
} from '@/types'

// Impact level badge colors
const impactColors: Record<ImpactLevel, 'danger' | 'warning' | 'secondary'> = {
  high: 'danger',
  medium: 'warning',
  low: 'secondary',
}

// Priority level badge colors
const priorityColors: Record<PriorityLevel, 'danger' | 'warning' | 'secondary' | 'default'> = {
  critical: 'danger',
  high: 'danger',
  medium: 'warning',
  low: 'secondary',
}

// Trend icons
const TrendIcon = ({ trend }: { trend: TrendDirection }) => {
  switch (trend) {
    case 'rising':
      return <TrendingUp className="h-3 w-3 text-green-500" />
    case 'declining':
      return <TrendingDown className="h-3 w-3 text-red-500" />
    default:
      return <Minus className="h-3 w-3 text-gray-400" />
  }
}

// Sentiment color
const sentimentColors = {
  positive: 'text-green-600',
  neutral: 'text-gray-500',
  negative: 'text-red-600',
}

// Insight card
function InsightCard({ insight }: { insight: KeyInsight }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-yellow-500" />
          <h4 className="font-medium">{insight.title}</h4>
        </div>
        <Badge variant={impactColors[insight.impact]}>
          {insight.impact === 'high' ? '높음' : insight.impact === 'medium' ? '중간' : '낮음'}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{insight.description}</p>
      <p className="mt-2 text-xs text-muted-foreground">관련 스레드: {insight.thread_count}개</p>
    </div>
  )
}

// Topic badge
function TopicBadge({ topic }: { topic: TrendingTopic }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2">
      <span className={`font-medium ${sentimentColors[topic.sentiment]}`}>{topic.topic}</span>
      <span className="text-xs text-muted-foreground">({topic.frequency})</span>
      <TrendIcon trend={topic.trend} />
    </div>
  )
}

// Pattern card
function PatternCard({ pattern }: { pattern: NotablePattern }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-blue-500" />
        <span className="text-sm font-medium">{pattern.user_segment}</span>
      </div>
      <p className="mt-2 text-sm">{pattern.pattern}</p>
      <p className="mt-1 text-xs text-muted-foreground">빈도: {pattern.frequency}</p>
      <p className="mt-2 text-xs italic text-blue-600">{pattern.implication}</p>
    </div>
  )
}

// Recommendation card
function RecommendationCard({ recommendation }: { recommendation: Recommendation }) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Target className="h-4 w-4 text-purple-500" />
          <h4 className="font-medium">{recommendation.action}</h4>
        </div>
        <Badge variant={priorityColors[recommendation.priority]}>
          {recommendation.priority === 'critical'
            ? '긴급'
            : recommendation.priority === 'high'
              ? '높음'
              : recommendation.priority === 'medium'
                ? '중간'
                : '낮음'}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">{recommendation.rationale}</p>
      <p className="mt-2 text-xs text-green-600">예상 효과: {recommendation.expected_impact}</p>
    </div>
  )
}

// History list item
function HistoryListItem({
  insight,
  isSelected,
  onClick,
}: {
  insight: AIInsightsSummary
  isSelected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full rounded-lg border p-3 text-left transition-colors',
        isSelected ? 'border-purple-500 bg-purple-50' : 'hover:bg-muted/50'
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">
          {format(parseISO(insight.analyzed_at), 'M월 d일 HH:mm', { locale: ko })}
        </span>
        <Badge variant="secondary" className="text-xs">
          {insight.threads_analyzed}개 스레드
        </Badge>
      </div>
      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{insight.summary}</p>
    </button>
  )
}

// Insights detail view
function InsightsDetail({ result, title }: { result: AIInsightsResult; title?: string }) {
  const [showInsights, setShowInsights] = useState(true)
  const [showPatterns, setShowPatterns] = useState(true)
  const [showRecommendations, setShowRecommendations] = useState(true)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">{title || '최근 24시간 분석'}</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {result.analyzed_at &&
              format(parseISO(result.analyzed_at), 'yyyy.MM.dd HH:mm', { locale: ko })}{' '}
            생성
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <MessageSquare className="h-8 w-8 text-purple-500" />
            <div>
              <p className="text-2xl font-bold">{result.threads_analyzed}</p>
              <p className="text-xs text-muted-foreground">분석된 스레드</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-3 p-4">
            <Clock className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{result.time_range_hours}시간</p>
              <p className="text-xs text-muted-foreground">분석 기간</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-500" />
            요약
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="leading-relaxed text-gray-700">{result.summary}</p>
        </CardContent>
      </Card>

      {/* Trending Topics */}
      <Card>
        <CardHeader>
          <CardTitle>인기 토픽</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {result.trending_topics.map((topic, idx) => (
              <TopicBadge key={idx} topic={topic} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Sentiment Overview */}
      <Card>
        <CardHeader>
          <CardTitle>감정 분석</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-green-500" />
              <span>긍정 {result.sentiment_overview.positive_percentage}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-gray-400" />
              <span>중립 {result.sentiment_overview.neutral_percentage}%</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-3 w-3 rounded-full bg-red-500" />
              <span>부정 {result.sentiment_overview.negative_percentage}%</span>
            </div>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            주요 감정: <span className="font-medium">{result.sentiment_overview.dominant_emotion}</span>
          </p>
          {result.sentiment_overview.notable_shift && (
            <div className="mt-3 rounded-lg border border-orange-200 bg-orange-50 p-3">
              <p className="text-sm text-orange-700">{result.sentiment_overview.notable_shift}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Key Insights */}
      <Card>
        <CardHeader>
          <button
            onClick={() => setShowInsights(!showInsights)}
            className="flex w-full items-center justify-between"
          >
            <CardTitle>핵심 인사이트</CardTitle>
            {showInsights ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </CardHeader>
        {showInsights && (
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {result.key_insights.map((insight, idx) => (
                <InsightCard key={idx} insight={insight} />
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Notable Patterns */}
      <Card>
        <CardHeader>
          <button
            onClick={() => setShowPatterns(!showPatterns)}
            className="flex w-full items-center justify-between"
          >
            <CardTitle>주목할 패턴</CardTitle>
            {showPatterns ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
          </button>
        </CardHeader>
        {showPatterns && (
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {result.notable_patterns.map((pattern, idx) => (
                <PatternCard key={idx} pattern={pattern} />
              ))}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Recommendations */}
      <Card>
        <CardHeader>
          <button
            onClick={() => setShowRecommendations(!showRecommendations)}
            className="flex w-full items-center justify-between"
          >
            <CardTitle>권장 조치</CardTitle>
            {showRecommendations ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </button>
        </CardHeader>
        {showRecommendations && (
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {result.recommendations.map((rec, idx) => (
                <RecommendationCard key={idx} recommendation={rec} />
              ))}
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  )
}

export function AIInsightsPage() {
  const [showHistory, setShowHistory] = useState(false)
  const [selectedHistoryId, setSelectedHistoryId] = useState<number | null>(null)
  const [historyPage, setHistoryPage] = useState(1)

  const { data, result, status, loading, error, refetch, generate, generating } = useAIInsights()
  const { data: historyData, isLoading: historyLoading } = useAIInsightsHistory(historyPage, 10)
  const { data: selectedInsight, isLoading: selectedLoading } = useAIInsightById(selectedHistoryId)

  const handleGenerate = () => {
    generate({ forceRefresh: false })
  }

  const handleForceRefresh = () => {
    generate({ forceRefresh: true })
  }

  const handleSelectHistory = (id: number) => {
    setSelectedHistoryId(id)
  }

  const handleBackToLatest = () => {
    setSelectedHistoryId(null)
  }

  if (loading) return <PageLoading />

  // Show selected history item
  if (selectedHistoryId && selectedInsight?.result) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={handleBackToLatest}>
            <ChevronLeft className="mr-1 h-4 w-4" />
            최신으로 돌아가기
          </Button>
        </div>
        <InsightsDetail
          result={selectedInsight.result}
          title={`${format(parseISO(selectedInsight.result.analyzed_at), 'M월 d일', { locale: ko })} 분석`}
        />
      </div>
    )
  }

  if (selectedHistoryId && selectedLoading) {
    return <PageLoading />
  }

  return (
    <div className="flex gap-6">
      {/* Main Content */}
      <div className="flex-1 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">AI 인사이트</h1>
            <p className="text-sm text-muted-foreground">
              최근 24시간 대화 데이터에서 AI가 추출한 인사이트입니다
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant={showHistory ? 'secondary' : 'outline'}
              onClick={() => setShowHistory(!showHistory)}
            >
              <History className="mr-2 h-4 w-4" />
              기록
            </Button>
            <Button variant="outline" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              새로고침
            </Button>
            <Button onClick={result ? handleForceRefresh : handleGenerate} disabled={generating}>
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {result ? '다시 생성' : '인사이트 생성'}
            </Button>
          </div>
        </div>

        {/* Content */}
        {error && !data ? (
          <Card className="flex h-96 items-center justify-center">
            <div className="text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
              <p className="mt-4 text-destructive">인사이트를 불러오는데 실패했습니다</p>
              <Button onClick={() => refetch()} className="mt-4">
                다시 시도
              </Button>
            </div>
          </Card>
        ) : status === 'processing' || status === 'received' ? (
          <Card className="flex h-96 items-center justify-center">
            <div className="text-center">
              <Loader2 className="mx-auto h-12 w-12 animate-spin text-purple-500" />
              <p className="mt-4 text-muted-foreground">인사이트 생성 중...</p>
              <p className="mt-1 text-xs text-muted-foreground">
                AI가 최근 24시간 대화를 분석하고 있습니다
              </p>
            </div>
          </Card>
        ) : status === 'failed' ? (
          <Card className="flex h-96 items-center justify-center">
            <div className="text-center">
              <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
              <p className="mt-4 text-destructive">인사이트 생성 실패</p>
              <p className="mt-2 text-sm text-muted-foreground">{data?.error_message}</p>
              <Button onClick={handleForceRefresh} className="mt-4">
                다시 생성
              </Button>
            </div>
          </Card>
        ) : result ? (
          <InsightsDetail result={result} />
        ) : (
          <Card className="flex h-96 items-center justify-center">
            <div className="text-center">
              <Sparkles className="mx-auto h-16 w-16 text-muted-foreground/30" />
              <p className="mt-4 text-muted-foreground">아직 생성된 인사이트가 없습니다</p>
              <p className="mt-1 text-xs text-muted-foreground">
                버튼을 눌러 최근 24시간 데이터를 분석해보세요
              </p>
              <Button onClick={handleGenerate} disabled={generating} className="mt-4">
                {generating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                인사이트 생성
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* History Sidebar */}
      {showHistory && (
        <div className="w-80 shrink-0">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <History className="h-4 w-4" />
                분석 기록
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {historyLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : historyData?.insights.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  아직 기록이 없습니다
                </p>
              ) : (
                <>
                  {historyData?.insights.map((insight) => (
                    <HistoryListItem
                      key={insight.id}
                      insight={insight}
                      isSelected={selectedHistoryId === insight.id}
                      onClick={() => handleSelectHistory(insight.id)}
                    />
                  ))}

                  {/* Pagination */}
                  {historyData && historyData.pagination.total_pages > 1 && (
                    <div className="flex items-center justify-between pt-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                        disabled={historyPage === 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        {historyPage} / {historyData.pagination.total_pages}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setHistoryPage((p) =>
                            Math.min(historyData.pagination.total_pages, p + 1)
                          )
                        }
                        disabled={historyPage === historyData.pagination.total_pages}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
